/*
 * SearchIntentDecoder
 * Helps a law firm decide which page type a search should land on.
 *
 * Rules (applied in this order):
 * 1. "Mentions a consultation, cost, hiring or calling" is on        -> Stage 3: Ready to hire
 * 2. "Names a lawyer or attorney" OR "Includes near me / a place" is on -> Stage 2: Comparing firms
 * 3. "Starts with a question" is on (and 1-2 are off)                -> Stage 1: Researching
 * 4. Nothing selected                                                -> no result yet
 * The optional "Read my search" button pre-ticks signals from the typed search using
 * simple word matching; the reader can change any toggle afterwards. Place names can't be
 * detected reliably, so the reader ticks "place" themselves.
 * No tracking, storage or network calls.
 */
import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";
 
type SignalKey = "question" | "legalRole" | "local" | "action";
 
const SIGNALS: { key: SignalKey; label: string; hint: string }[] = [
  { key: "question", label: "Starts with a question", hint: "what, how, can, should, do I…" },
  { key: "legalRole", label: "Names a lawyer or attorney", hint: "lawyer, attorney, law firm" },
  { key: "local", label: "Includes “near me” or a place", hint: "near me, a city, a county" },
  { key: "action", label: "Mentions a consultation, cost, hiring or calling", hint: "free consultation, fees, hire, call" },
];
 
type Stage = {
  name: string;
  page: string;
  nextStep: string;
  links: { href: string; text: string }[];
};
 
const STAGES: Record<1 | 2 | 3, Stage> = {
  1: {
    name: "Stage 1: Researching",
    page: "A helpful guide or blog post that answers the question properly.",
    nextStep:
      "Link from the guide to the related practice-area page, so readers who are ready can keep going without searching again.",
    links: [{ href: "/services/legal-content-writing", text: "See how we plan legal content" }],
  },
  2: {
    name: "Stage 2: Comparing firms",
    page: "A practice-area page (or a location page if the search names a place).",
    nextStep:
      "Show who handles these cases, why the firm is a good fit, and how the consultation works, with contact options visible near the top.",
    links: [
      { href: "/practice-areas", text: "See practice-area SEO by type of law" },
      { href: "/services/local-seo-for-law-firms", text: "See local SEO for law firms" },
    ],
  },
  3: {
    name: "Stage 3: Ready to hire",
    page: "A location or consultation page with the contact options right there.",
    nextStep:
      "Make calling, the form and booking obvious, and explain what happens after someone reaches out.",
    links: [{ href: "/services/lead-generation-for-law-firms", text: "See lead generation for law firms" }],
  },
};
 
function detect(query: string): Record<SignalKey, boolean> {
  const q = query.trim().toLowerCase();
  return {
    question: /^(what|how|can|should|do|does|is|are|when|why|who|will|which)\b/.test(q) || q.includes("?"),
    legalRole: /\b(lawyers?|attorneys?|law firms?|counsel|solicitors?)\b/.test(q),
    local: /\b(near me|nearby|close to me|local)\b/.test(q),
    action: /\b(consultation|consult|hire|hiring|cost|costs|fees?|price|call|book|free)\b/.test(q),
  };
}
 
function getStage(s: Record<SignalKey, boolean>): 1 | 2 | 3 | null {
  if (s.action) return 3;
  if (s.legalRole || s.local) return 2;
  if (s.question) return 1;
  return null;
}
 
export default function SearchIntentDecoder() {
  const inputId = useId();
  const [query, setQuery] = useState("");
  const [signals, setSignals] = useState<Record<SignalKey, boolean>>({
    question: false,
    legalRole: false,
    local: false,
    action: false,
  });
 
  const stage = getStage(signals);
  const result = stage ? STAGES[stage] : null;
 
  const toggle = (key: SignalKey) => setSignals((prev) => ({ ...prev, [key]: !prev[key] }));
 
  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
 
  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Which page should this search land on?</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Type a search your clients might use, or just tick what it contains.
      </p>
 
      <div className="mt-4">
        <label htmlFor={inputId} className="block text-sm font-medium">
          The search (optional)
        </label>
        <div className="mt-1 flex flex-col gap-2 sm:flex-row">
          <input
            id={inputId}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. divorce lawyer near me"
            className={`w-full rounded-md border border-input bg-card px-3 py-2 text-sm ${focusRing}`}
          />
          <button
            type="button"
            onClick={() => setSignals(detect(query))}
            disabled={query.trim().length === 0}
            className={`rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50 ${focusRing}`}
          >
            Read my search
          </button>
        </div>
      </div>
 
      <fieldset className="mt-5">
        <legend className="text-sm font-medium">What does the search contain?</legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {SIGNALS.map((s) => (
            <button
              key={s.key}
              type="button"
              aria-pressed={signals[s.key]}
              onClick={() => toggle(s.key)}
              className={`rounded-lg border px-3 py-2 text-left text-sm ${focusRing} ${
                signals[s.key]
                  ? "border-border bg-primary text-primary-foreground"
                  : "border-input bg-card text-card-foreground"
              }`}
            >
              <span className="block font-medium">{s.label}</span>
              <span className={`block text-xs ${signals[s.key] ? "" : "text-muted-foreground"}`}>
                {s.hint}
              </span>
            </button>
          ))}
        </div>
      </fieldset>
 
      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4">
        {result ? (
          <div>
            <p className="text-sm font-semibold">{result.name}</p>
            <p className="mt-2 text-sm">
              <span className="font-medium">Land it on: </span>
              {result.page}
            </p>
            <p className="mt-1 text-sm">
              <span className="font-medium">Next step on that page: </span>
              {result.nextStep}
            </p>
            <ul className="mt-3 space-y-1">
              {result.links.map((l) => (
                <li key={l.href}>
                  <Link to={l.href} className={`text-sm font-medium underline underline-offset-4 ${focusRing}`}>
                    {l.text} →
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Tick at least one signal to see where this search should land.</p>
        )}
      </div>
    </section>
  );
}
